import { IPromotion } from 'common/promotion';
import { IItinerary, IKeyStringValue } from 'common/types';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import { TripType } from 'contexts/searchParams';
import useScroll, { internationalListComponentId } from 'hooks/useScroll';
import { IFareTypeList, ISchedule } from 'list/international/types';
import React, { useContext, useEffect, useState } from 'react';
import AgentList from './agentList';
import { IOta, IPassengerCount } from './types';
import {
  areaCode,
  findFareList,
  getCardFareTypeMap,
  getCheckedCardFareTypeName,
  getUncheckedCount,
} from './utils';

interface IDetailAgentSelectProps {
  itineraryList: IItinerary[];
  korFareTypes: IKeyStringValue;
  latestCardFareType: string;
  loaded: boolean;
  passengerCount: IPassengerCount;
  promotions: IPromotion[];
  refetchRef: React.MutableRefObject<() => void>;
  seatClass: string;
  selectedCardFares: IFareTypeList;
  selectedLabels: string[][];
  selectedScheduleIds: string[];
  selectedSchedulesData: ISchedule[];
  trip: TripType;
  handleBooking: ({ otaId, agreeStatus, bookingUrl }: IOta) => () => void;
  setMinFare: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const DetailAgentSelect = ({
  itineraryList,
  korFareTypes,
  latestCardFareType,
  loaded,
  passengerCount: { adultCnt, childCnt, infantCnt },
  promotions,
  refetchRef,
  seatClass,
  selectedCardFares,
  selectedLabels,
  selectedScheduleIds,
  selectedSchedulesData,
  trip,
  handleBooking,
  setMinFare,
}: IDetailAgentSelectProps) => {
  const setModal = useContext(ModalFuncContext);
  const { restoreScroll } = useScroll(internationalListComponentId);

  // {all:false, A01:true, A01/B05:false, ...}
  const cardFareTypeMap = getCardFareTypeMap(
    selectedCardFares,
    latestCardFareType,
  );

  // 체크 카드요금조건
  const [checkCardFareTypeMap, setCheckCardFareOption] =
    useState(cardFareTypeMap);
  // 여행사 더보기
  const [isMore, setIsMore] = useState(false);

  const closeModal = () => {
    setModal({
      type: Modal.NULL,
      position: ModalPosition.DETAIL,
    });
  };

  // 다른 요건 선택시 모달 레이어
  const showOthers = () => {
    setCheckCardFareOption(checkCardFareTypeMap);
    setModal({
      type: Modal.CARD_FARE_TYPES,
      position: ModalPosition.DETAIL,
      payload: {
        departureDate: itineraryList[0].departureDate,
        korFareTypes,
        passengerCount: { adultCnt, childCnt, infantCnt },
        prevCardFareMap: checkCardFareTypeMap,
        promotions,
        selectedCardFares,
        selectedSchedules: selectedSchedulesData.map(({ detail }) => detail),
        closeModal,
        restoreScroll,
        setCheckCardFareOption,
        setIsMore,
      },
    });
  };

  // 카드요금조건으로 선택한거 get
  const fareDataList = findFareList(
    checkCardFareTypeMap,
    selectedCardFares,
    adultCnt,
    childCnt,
    infantCnt,
  );

  useEffect(() => {
    setCheckCardFareOption(cardFareTypeMap);
  }, [selectedCardFares]);
  const uncheckedCount = getUncheckedCount(checkCardFareTypeMap);

  return (
    <div className="conditions">
      <div className="heading">
        <strong className="current">
          {getCheckedCardFareTypeName(checkCardFareTypeMap, korFareTypes)}
        </strong>
        {Object.keys(checkCardFareTypeMap)?.length > 2 && (
          <button
            type="button"
            className="change"
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.faretype`}
            onClick={showOthers}
          >
            <span className="txt">다른 요금 조건 선택&nbsp;</span>
            {uncheckedCount && <b className="count">{uncheckedCount}</b>}
          </button>
        )}
      </div>
      {(fareDataList.length > 0 || !loaded) && ( // 판매사 리스트
        <AgentList
          fareDataList={fareDataList}
          isMore={isMore}
          itineraryList={itineraryList}
          korFareTypes={korFareTypes}
          loaded={loaded}
          passengerCount={{ adultCnt, childCnt, infantCnt }}
          promotions={promotions}
          refetchRef={refetchRef}
          seatClass={seatClass}
          selectedLabels={selectedLabels}
          selectedScheduleIds={selectedScheduleIds}
          selectedSchedulesData={selectedSchedulesData}
          trip={trip}
          handleBooking={handleBooking}
          setIsMore={setIsMore}
          setMinFare={setMinFare}
        />
      )}
    </div>
  );
};

export default DetailAgentSelect;
